class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    function _defineProperty(obj, key, value) {
      if (key in obj) {
        Object.defineProperty(obj, key, {
          value,
          enumerable: true,
          configurable: true,
          writable: true,
        });
      } else {
        obj[key] = value;
      }
      return obj;
    }

    _defineProperty(this, "setPopularSearchesLink", () => {
      const popularSearchItems = document.querySelectorAll('[data-ps-item]');
      popularSearchItems.forEach(itm => itm.href = createSearchLink(itm.dataset.psQuery));
    });

    this.selectors = {
      searchQuery: '[data-query]',
      searchMessage: '[data-message]',
      input: '[data-search-input]',
      submit: 'button[type="submit"]',
      loading: '[data-spinner]',
      clear: '[data-clear-search]',
      popularSearchItems: ['[data-ps-item]'],
      searchResults: '[data-predictive-search]'
    };

    this.container = document.querySelector('[data-search-container]');
    this.domNodes = queryDomNodes(this.selectors, this.container);
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.popularSearches = this.querySelector('[data-popular-searches]');
    this.searchCount = this.querySelector('[data-search-count]');

    // Lazy init: defer event listeners and popular links until first focus
    this._initialized = false;
    if (this.input) {
      this.input.addEventListener('focus', () => this._lazyInit(), { once: true });
    }
  }

  _lazyInit() {
    if (this._initialized) return;
    this._initialized = true;
    this.setupEventListeners();
    this.setPopularSearchesLink();
  }

  setupEventListeners() {
    const { clear } = this.domNodes;
    const form = this.querySelector('form.m-search-form');
    form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('input', debounce(event => {
      this.onChange(event);
    }, 300).bind(this));
    clear.addEventListener('click', this.onClearSearch.bind(this));

    /* Close dropdown when clicking outside */
    document.addEventListener('click', (e) => {
      if (this.isOpen && !this.contains(e.target)) {
        this.close();
      }
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onClearSearch(event) {
    event.preventDefault();
    this.input.value = '';
    this.onChange();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(false);
    const searchByTag = this.dataset.searchByTag === 'true';
    const searchByBody = this.dataset.searchByBody === 'true';
    const unavailableProductsOption = this.dataset.unavailableProductsOption;
    let searchFields = 'title,product_type,vendor,variants.sku,variants.title';
    if (searchByTag) searchFields += ',body';
    if (searchByBody) searchFields += ',tag';

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    let searchURL = `${window.MinimogSettings.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[options][unavailable_products]=${unavailableProductsOption}&resources[options][fields]=${searchFields}&section_id=predictive-search`;
    fetch(searchURL).then(response => {
      if (!response.ok) {
        var error = new Error(response.status);
        this.close();
        throw error;
      }

      return response.text();
    }).then(text => {
      const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
      this.cachedResults[queryKey] = resultsMarkup;
      this.renderSearchResults(resultsMarkup);
    }).catch(error => {
      this.close();
      throw error;
    });
  }

  /* Country → Collection mapping */
  _getCollectionHint(q) {
    if (!q || q.length < 2) return null;
    q = q.toLowerCase().trim();
    var EU={url:'/collections/europe-uk-esims',title:'Europe & UK eSIMs',flag:'🇪🇺'},
        US={url:'/collections/north-america-esims',title:'USA & Canada eSIMs',flag:'🇺🇸'},
        JP={url:'/products/japan-esim-card',title:'Japan eSIM',flag:'🇯🇵'},
        SE={url:'/collections/southeast-asia-esim',title:'South East Asia eSIMs',flag:'🌏'},
        CN={url:'/collections/esim-china',title:'China & HK eSIMs',flag:'🇨🇳'},
        AU={url:'/products/australia-travel-prepaid-esim',title:'Australia eSIM',flag:'🇦🇺'},
        KR={url:'/products/south-korea-prepaid-travel-esim-card',title:'South Korea eSIM',flag:'🇰🇷'},
        NZ={url:'/products/new-zealand-prepaid-travel-esim',title:'New Zealand eSIM',flag:'🇳🇿'};
    var m={
      france:EU,germany:EU,italy:EU,spain:EU,portugal:EU,netherlands:EU,belgium:EU,
      austria:EU,greece:EU,ireland:EU,poland:EU,sweden:EU,denmark:EU,finland:EU,
      norway:EU,switzerland:EU,czech:EU,hungary:EU,romania:EU,croatia:EU,bulgaria:EU,
      slovakia:EU,slovenia:EU,estonia:EU,latvia:EU,lithuania:EU,luxembourg:EU,malta:EU,
      cyprus:EU,iceland:EU,uk:EU,'united kingdom':EU,england:EU,scotland:EU,wales:EU,
      europe:EU,eu:EU,schengen:EU,turkey:EU,
      '法国':EU,'德国':EU,'意大利':EU,'西班牙':EU,'葡萄牙':EU,'荷兰':EU,'比利时':EU,
      '奥地利':EU,'希腊':EU,'爱尔兰':EU,'波兰':EU,'瑞典':EU,'丹麦':EU,'芬兰':EU,
      '挪威':EU,'瑞士':EU,'捷克':EU,'匈牙利':EU,'英国':EU,'欧洲':EU,'欧盟':EU,'土耳其':EU,
      usa:US,'united states':US,america:US,canada:US,mexico:US,'美国':US,'加拿大':US,
      japan:JP,'日本':JP,
      thailand:SE,vietnam:SE,indonesia:SE,bali:SE,philippines:SE,malaysia:SE,singapore:SE,
      cambodia:SE,laos:SE,'泰国':SE,'越南':SE,'印尼':SE,'菲律宾':SE,'马来西亚':SE,'新加坡':SE,
      china:CN,'hong kong':CN,'中国':CN,'香港':CN,
      australia:AU,'澳大利亚':AU,'澳洲':AU,
      korea:KR,'south korea':KR,'韩国':KR,
      'new zealand':NZ,'新西兰':NZ
    };
    for (var k in m) { if (q.indexOf(k)!==-1||k.indexOf(q)!==-1) return m[k]; }
    return null;
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    const searchItemsWrapper = this.querySelector('[data-search-items-wrapper]');

    /* Inject collection suggestion card if query matches a country */
    var query = this.input ? this.input.value : '';
    var hint = this._getCollectionHint(query);
    if (hint) {
      var card = document.createElement('a');
      card.href = hint.url;
      card.className = 'ps-collection-hint';
      card.style.cssText = 'display:flex;align-items:center;gap:12px;padding:12px 16px;background:#fffbeb;border:1px solid #fcd34d;border-radius:8px;margin:8px 0 4px;text-decoration:none;color:#1e293b;font-family:Jost,sans-serif;';
      card.innerHTML = '<span style="font-size:28px">' + hint.flag + '</span><div><div style="font-weight:600;font-size:0.95rem">' + hint.title + '</div><div style="font-size:0.8rem;color:#6b7280">View collection →</div></div>';
      var target = searchItemsWrapper || this.predictiveSearchResults;
      target.insertBefore(card, target.firstChild);
    }

    var hasResults = (searchItemsWrapper && searchItemsWrapper.childElementCount > 0) || hint;
    if (hasResults) {
      this.renderSearchQueryAndMessage(true);
    } else {
      this.renderSearchQueryAndMessage(false);
    }

    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);
    this.open();
  }

  renderSearchQueryAndMessage(results) {
    const { input, searchQuery, searchMessage } = this.domNodes;
    const query = input.value;
    const { resultsTitle } = searchMessage.dataset;
    searchQuery.textContent = query;

    if (results) {
      searchMessage.textContent = resultsTitle;
    } else {
      searchMessage.textContent = searchMessage.dataset.noResults;
    }
  }

  toggleSpinnerLoading(show) {
    const { loading, submit } = this.domNodes;
    submit.style.visibility = show ? 'hidden' : 'visible';
    loading.style.visibility = show ? 'visible' : 'hidden';
  }

  toggleClearSearch(show) {
    const { clear } = this.domNodes;
    clear.style.visibility = show ? "visible" : "hidden";
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this.predictiveSearchResults.classList.remove('hidden');
    this.predictiveSearchResults.classList.add('is-open');
    this.searchCount.classList.remove('hidden');
  }

  close() {
    let clearSearchTerm = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.toggleClearSearch(false);
    }

    this.removeAttribute('open');
    this.predictiveSearchResults.classList.add('hidden');
    this.predictiveSearchResults.classList.remove('is-open');
    this.popularSearches.classList.remove('hidden');
    this.searchCount.classList.add('hidden');
  }
}

customElements.define('predictive-search', PredictiveSearch);
